import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
  Dumbbell, Plus, Play, Square, Trophy, TrendingUp,
  Calendar, ChevronRight, ChevronDown, ChevronUp, Check,
  Flame, Target, Clock, ArrowLeft, X, Search, Minus,
  RotateCcw, Ruler, Weight, Activity, Star, ChevronLeft,
  Zap, Copy, Layers
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const FULL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const FitnessTracker = () => {
  const { token } = useAuth();
  const [tab, setTab] = useState('today');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/fitness/stats`, { headers });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => {
    fetchStats().finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-uf-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-uf-red" />
      </div>
    );
  }

  const tabs = [
    { key: 'today', label: 'Today', icon: Flame },
    { key: 'plans', label: 'Plans', icon: Calendar },
    { key: 'history', label: 'History', icon: Clock },
    { key: 'pbs', label: 'PRs', icon: Trophy },
    { key: 'body', label: 'Body', icon: Ruler },
  ];

  return (
    <div className="min-h-screen bg-uf-black pb-20" data-testid="fitness-tracker">
      {/* Header */}
      <div className="bg-gradient-to-b from-uf-red/20 to-uf-black px-4 pt-6 pb-4">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-4">
            <Link to="/hub" className="text-uf-text-muted hover:text-uf-text" data-testid="back-to-hub">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="font-heading text-2xl text-uf-text font-bold tracking-wide">WORKOUTS</h1>
            <div className="w-6" />
          </div>
          {/* Stats Row */}
          {stats && (
            <div className="grid grid-cols-4 gap-2" data-testid="fitness-stats-bar">
              <div className="bg-uf-surface/60 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-uf-text">{stats.total_sessions}</p>
                <p className="text-[10px] text-uf-text-muted uppercase">Sessions</p>
              </div>
              <div className="bg-uf-surface/60 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-uf-text">{stats.total_sets}</p>
                <p className="text-[10px] text-uf-text-muted uppercase">Sets</p>
              </div>
              <div className="bg-uf-surface/60 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-uf-red">{stats.total_pbs}</p>
                <p className="text-[10px] text-uf-text-muted uppercase">PRs</p>
              </div>
              <div className="bg-uf-surface/60 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-uf-text">{stats.current_weight ? `${stats.current_weight}` : '--'}</p>
                <p className="text-[10px] text-uf-text-muted uppercase">kg</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tab Bar */}
      <div className="sticky top-0 z-30 bg-uf-black/95 backdrop-blur border-b border-uf-border">
        <div className="max-w-lg mx-auto flex">
          {tabs.map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex-1 py-3 flex flex-col items-center gap-1 text-xs font-medium transition-colors ${
                  active ? 'text-uf-red border-b-2 border-uf-red' : 'text-uf-text-muted'
                }`}
                data-testid={`tab-${t.key}`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-4 pt-4 max-w-lg mx-auto">
        {tab === 'today' && <TodayTab headers={headers} onStatsChange={fetchStats} />}
        {tab === 'plans' && <PlansTab headers={headers} />}
        {tab === 'history' && <HistoryTab headers={headers} />}
        {tab === 'pbs' && <PersonalBestsTab headers={headers} />}
        {tab === 'body' && <BodyTab headers={headers} onStatsChange={fetchStats} />}
      </div>

      {/* Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-uf-surface border-t border-uf-border z-40">
        <div className="max-w-lg mx-auto flex justify-around py-3">
          <Link to="/hub" className="flex flex-col items-center text-uf-text-muted hover:text-uf-red">
            <Activity className="w-6 h-6" />
            <span className="text-xs mt-1">Home</span>
          </Link>
          <Link to="/hub/fitness" className="flex flex-col items-center text-uf-red">
            <Dumbbell className="w-6 h-6" />
            <span className="text-xs mt-1">Workouts</span>
          </Link>
          <Link to="/hub/profile" className="flex flex-col items-center text-uf-text-muted hover:text-uf-red">
            <Target className="w-6 h-6" />
            <span className="text-xs mt-1">Profile</span>
          </Link>
        </div>
      </nav>
    </div>
  );
};

/* ============================================
   TODAY TAB - Start workout, log sets on the fly
   ============================================ */
const TodayTab = ({ headers, onStatsChange }) => {
  const [todayPlan, setTodayPlan] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [exerciseLogs, setExerciseLogs] = useState({});
  const [loading, setLoading] = useState(true);
  const [prAlert, setPrAlert] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    fetchTodayPlan();
  }, []);

  // Timer for active session
  useEffect(() => {
    if (!activeSession || activeSession.finished_at) return;
    const start = new Date(activeSession.started_at).getTime();
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [activeSession]);

  const fetchTodayPlan = async () => {
    try {
      const res = await fetch(`${API}/api/fitness/plans/today`, { headers });
      if (res.ok) setTodayPlan(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const startWorkout = async () => {
    try {
      const body = todayPlan?.plan_id ? {
        plan_id: todayPlan.plan_id,
        day_id: todayPlan.day?.id,
        day_label: todayPlan.day?.label
      } : { day_label: 'Quick Workout' };
      const res = await fetch(`${API}/api/fitness/sessions`, {
        method: 'POST', headers, body: JSON.stringify(body)
      });
      if (res.ok) {
        const session = await res.json();
        setActiveSession(session);
        // Pre-populate exercise logs from today's plan
        if (todayPlan?.exercises) {
          const logs = {};
          todayPlan.exercises.forEach(ex => {
            logs[ex.exercise_key] = {
              name: ex.exercise_name,
              target_sets: ex.target_sets,
              target_reps: ex.target_reps,
              sets: []
            };
          });
          setExerciseLogs(logs);
        }
      }
    } catch (e) { console.error(e); }
  };

  const finishWorkout = async () => {
    if (!activeSession) return;
    try {
      const res = await fetch(`${API}/api/fitness/sessions/${activeSession.id}/finish`, {
        method: 'POST', headers
      });
      if (res.ok) {
        setActiveSession(await res.json());
        onStatsChange();
      }
    } catch (e) { console.error(e); }
  };

  const logSet = async (exerciseKey, exerciseName, setNumber, reps, weight) => {
    if (!activeSession) return;
    try {
      const res = await fetch(`${API}/api/fitness/sets`, {
        method: 'POST', headers,
        body: JSON.stringify({
          session_id: activeSession.id,
          exercise_key: exerciseKey,
          exercise_name: exerciseName,
          set_number: setNumber,
          reps: reps ? parseInt(reps) : null,
          weight: weight ? parseFloat(weight) : null
        })
      });
      if (res.ok) {
        const result = await res.json();
        setExerciseLogs(prev => {
          const ex = prev[exerciseKey] || { name: exerciseName, sets: [] };
          return {
            ...prev,
            [exerciseKey]: {
              ...ex,
              sets: [...ex.sets, result]
            }
          };
        });
        if (result.is_pr) {
          setPrAlert(exerciseName);
          setTimeout(() => setPrAlert(null), 3000);
        }
      }
    } catch (e) { console.error(e); }
  };

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
      ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
      : `${m}:${String(sec).padStart(2, '0')}`;
  };

  if (loading) return <div className="text-center py-8 text-uf-text-muted">Loading...</div>;

  // PR Alert overlay
  const prOverlay = prAlert && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in" data-testid="pr-alert">
      <div className="bg-uf-surface border-2 border-yellow-500 rounded-2xl p-8 text-center mx-4 animate-bounce">
        <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-3" />
        <h3 className="text-2xl font-bold text-yellow-500 font-heading">NEW PR!</h3>
        <p className="text-uf-text mt-2">{prAlert}</p>
      </div>
    </div>
  );

  // Active Session View
  if (activeSession && !activeSession.finished_at) {
    return (
      <div data-testid="active-session">
        {prOverlay}
        {/* Session Header */}
        <div className="bg-uf-surface rounded-xl border border-uf-red/50 p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-uf-red text-sm font-semibold flex items-center">
                <span className="w-2 h-2 bg-uf-red rounded-full animate-pulse mr-2" />
                LIVE WORKOUT
              </p>
              <p className="text-uf-text font-bold text-lg">{activeSession.day_label || 'Workout'}</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-mono font-bold text-uf-text" data-testid="session-timer">{formatTime(elapsed)}</p>
              <p className="text-uf-text-muted text-xs">{Object.values(exerciseLogs).reduce((t, e) => t + e.sets.length, 0)} sets logged</p>
            </div>
          </div>
        </div>

        {/* Exercise Cards */}
        <div className="space-y-3">
          {Object.entries(exerciseLogs).map(([key, ex]) => (
            <ExerciseLogCard
              key={key}
              exerciseKey={key}
              exerciseName={ex.name}
              targetSets={ex.target_sets}
              targetReps={ex.target_reps}
              loggedSets={ex.sets}
              onLogSet={logSet}
            />
          ))}
        </div>

        {/* Add Exercise Button */}
        <AddExerciseButton
          headers={headers}
          existingKeys={Object.keys(exerciseLogs)}
          onAdd={(key, name) => {
            setExerciseLogs(prev => ({
              ...prev,
              [key]: { name, target_sets: 3, target_reps: '10', sets: [] }
            }));
          }}
        />

        {/* Finish Button */}
        <button
          onClick={finishWorkout}
          className="w-full mt-6 py-4 bg-uf-red text-white font-bold rounded-xl text-lg flex items-center justify-center gap-2 hover:bg-uf-red-hover transition-colors"
          data-testid="finish-workout-btn"
        >
          <Square className="w-5 h-5" />
          Finish Workout
        </button>
      </div>
    );
  }

  // Completed Session Summary
  if (activeSession && activeSession.finished_at) {
    const totalSets = activeSession.exercises?.reduce((t, e) => t + e.sets.length, 0) || 0;
    const startTime = new Date(activeSession.started_at);
    const endTime = new Date(activeSession.finished_at);
    const duration = Math.floor((endTime - startTime) / 1000);
    return (
      <div data-testid="session-summary">
        <div className="bg-uf-surface rounded-xl border border-green-500/30 p-6 text-center mb-4">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <Check className="w-8 h-8 text-green-500" />
          </div>
          <h3 className="text-xl font-bold text-uf-text font-heading">WORKOUT COMPLETE</h3>
          <p className="text-uf-text-muted mt-1">{activeSession.day_label}</p>
          <div className="flex justify-center gap-6 mt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-uf-text">{formatTime(duration)}</p>
              <p className="text-xs text-uf-text-muted">Duration</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-uf-text">{totalSets}</p>
              <p className="text-xs text-uf-text-muted">Sets</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-uf-text">{activeSession.exercises?.length || 0}</p>
              <p className="text-xs text-uf-text-muted">Exercises</p>
            </div>
          </div>
        </div>
        <button
          onClick={() => { setActiveSession(null); setExerciseLogs({}); }}
          className="w-full py-3 bg-uf-surface border border-uf-border text-uf-text rounded-xl font-medium"
          data-testid="new-workout-btn"
        >
          Start New Workout
        </button>
      </div>
    );
  }

  // Default: Start Workout View
  return (
    <div data-testid="today-view">
      {todayPlan?.plan_name ? (
        <div className="bg-uf-surface rounded-xl border border-uf-border p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-uf-text-muted text-xs uppercase tracking-wider">Today's Plan</p>
              <h3 className="text-lg font-bold text-uf-text">{todayPlan.plan_name}</h3>
            </div>
            {todayPlan.day?.is_rest_day ? (
              <span className="text-sm px-3 py-1 bg-green-500/20 text-green-400 rounded-full">Rest Day</span>
            ) : (
              <span className="text-sm px-3 py-1 bg-uf-red/20 text-uf-red rounded-full">{todayPlan.day?.label}</span>
            )}
          </div>

          {!todayPlan.day?.is_rest_day && todayPlan.exercises?.length > 0 && (
            <div className="space-y-2 mb-4">
              {todayPlan.exercises.map((ex, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-uf-border/50 last:border-0">
                  <div className="w-8 h-8 rounded-lg bg-uf-red/10 flex items-center justify-center">
                    <Dumbbell className="w-4 h-4 text-uf-red" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-uf-text truncate">{ex.exercise_name}</p>
                    <p className="text-xs text-uf-text-muted">{ex.target_sets} sets x {ex.target_reps} reps</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!todayPlan.day?.is_rest_day && (
            <button
              onClick={startWorkout}
              className="w-full py-4 bg-uf-red text-white font-bold rounded-xl text-lg flex items-center justify-center gap-2 hover:bg-uf-red-hover transition-colors"
              data-testid="start-workout-btn"
            >
              <Play className="w-5 h-5" />
              Start Workout
            </button>
          )}
        </div>
      ) : (
        <div className="bg-uf-surface rounded-xl border border-uf-border p-6 text-center">
          <Dumbbell className="w-12 h-12 text-uf-text-muted mx-auto mb-3" />
          <h3 className="text-lg font-bold text-uf-text mb-1">No Plan Set</h3>
          <p className="text-sm text-uf-text-muted mb-4">Create a workout plan or start a quick session</p>
          <div className="flex gap-2">
            <button
              onClick={startWorkout}
              className="flex-1 py-3 bg-uf-red text-white font-semibold rounded-xl flex items-center justify-center gap-2 hover:bg-uf-red-hover"
              data-testid="quick-workout-btn"
            >
              <Play className="w-4 h-4" />
              Quick Workout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================================
   EXERCISE LOG CARD - Log sets in real-time
   ============================================ */
const ExerciseLogCard = ({ exerciseKey, exerciseName, targetSets, targetReps, loggedSets, onLogSet }) => {
  const [expanded, setExpanded] = useState(true);
  const [reps, setReps] = useState('');
  const [weight, setWeight] = useState('');
  const nextSet = loggedSets.length + 1;

  const handleLog = () => {
    onLogSet(exerciseKey, exerciseName, nextSet, reps, weight);
    setReps('');
    setWeight('');
  };

  return (
    <div className="bg-uf-surface rounded-xl border border-uf-border overflow-hidden" data-testid={`exercise-card-${exerciseKey}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-uf-red/10 flex items-center justify-center">
            <Dumbbell className="w-4 h-4 text-uf-red" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-uf-text">{exerciseName}</p>
            <p className="text-xs text-uf-text-muted">
              {loggedSets.length}/{targetSets || '?'} sets
              {targetReps && ` - ${targetReps} reps`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {loggedSets.some(s => s.is_pr) && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
          {expanded ? <ChevronUp className="w-4 h-4 text-uf-text-muted" /> : <ChevronDown className="w-4 h-4 text-uf-text-muted" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-uf-border/50">
          {/* Logged sets */}
          {loggedSets.length > 0 && (
            <div className="mt-3 space-y-1">
              <div className="grid grid-cols-4 text-xs text-uf-text-muted px-1 pb-1">
                <span>SET</span><span>WEIGHT</span><span>REPS</span><span></span>
              </div>
              {loggedSets.map((s, i) => (
                <div key={i} className={`grid grid-cols-4 text-sm py-1.5 px-1 rounded ${s.is_pr ? 'bg-yellow-500/10 border border-yellow-500/30' : ''}`}>
                  <span className="text-uf-text-muted">{s.set_number}</span>
                  <span className="text-uf-text font-medium">{s.weight ? `${s.weight}kg` : '-'}</span>
                  <span className="text-uf-text font-medium">{s.reps || '-'}</span>
                  <span>{s.is_pr && <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />}</span>
                </div>
              ))}
            </div>
          )}

          {/* Input row */}
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-uf-text-muted w-8 text-center">{nextSet}</span>
            <input
              type="number"
              placeholder="kg"
              value={weight}
              onChange={e => setWeight(e.target.value)}
              className="flex-1 bg-uf-black border border-uf-border rounded-lg px-3 py-2.5 text-sm text-uf-text text-center focus:border-uf-red outline-none"
              data-testid={`weight-input-${exerciseKey}`}
            />
            <input
              type="number"
              placeholder="reps"
              value={reps}
              onChange={e => setReps(e.target.value)}
              className="flex-1 bg-uf-black border border-uf-border rounded-lg px-3 py-2.5 text-sm text-uf-text text-center focus:border-uf-red outline-none"
              data-testid={`reps-input-${exerciseKey}`}
            />
            <button
              onClick={handleLog}
              className="bg-uf-red text-white px-4 py-2.5 rounded-lg font-semibold text-sm hover:bg-uf-red-hover transition-colors"
              data-testid={`log-set-btn-${exerciseKey}`}
            >
              <Check className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================================
   ADD EXERCISE BUTTON
   ============================================ */
const AddExerciseButton = ({ headers, existingKeys, onAdd }) => {
  const [showModal, setShowModal] = useState(false);
  const [exercises, setExercises] = useState([]);
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');

  useEffect(() => {
    if (showModal && exercises.length === 0) {
      fetch(`${API}/api/fitness/exercises`, { headers })
        .then(r => r.json())
        .then(d => setExercises(d.exercises || []))
        .catch(() => {});
    }
  }, [showModal]);

  const filtered = exercises.filter(e =>
    !existingKeys.includes(e.key) &&
    (groupFilter === 'all' || e.group === groupFilter) &&
    e.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="w-full mt-3 py-3 border-2 border-dashed border-uf-border rounded-xl text-uf-text-muted hover:border-uf-red hover:text-uf-red transition-colors flex items-center justify-center gap-2"
        data-testid="add-exercise-btn"
      >
        <Plus className="w-4 h-4" />
        Add Exercise
      </button>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center">
          <div className="w-full max-w-lg bg-uf-surface border border-uf-border rounded-t-2xl sm:rounded-2xl max-h-[80vh] flex flex-col" data-testid="exercise-picker-modal">
            <div className="p-4 border-b border-uf-border flex items-center justify-between">
              <h3 className="text-lg font-bold text-uf-text">Add Exercise</h3>
              <button onClick={() => setShowModal(false)} className="text-uf-text-muted">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 border-b border-uf-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-uf-text-muted" />
                <input
                  type="text"
                  placeholder="Search exercises..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full bg-uf-black border border-uf-border rounded-lg pl-10 pr-4 py-2.5 text-sm text-uf-text focus:border-uf-red outline-none"
                  data-testid="exercise-search-input"
                />
              </div>
              <div className="flex gap-1.5 mt-3 overflow-x-auto pb-1">
                {['all', 'chest', 'back', 'shoulders', 'arms', 'legs', 'core', 'cardio'].map(g => (
                  <button
                    key={g}
                    onClick={() => setGroupFilter(g)}
                    className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                      groupFilter === g ? 'bg-uf-red text-white' : 'bg-uf-black text-uf-text-muted'
                    }`}
                  >
                    {g === 'all' ? 'All' : g.charAt(0).toUpperCase() + g.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {filtered.map(ex => (
                <button
                  key={ex.key}
                  onClick={() => { onAdd(ex.key, ex.name); setShowModal(false); }}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-uf-surface-hi transition-colors text-left"
                  data-testid={`exercise-option-${ex.key}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-uf-red/10 flex items-center justify-center shrink-0">
                    <Dumbbell className="w-4 h-4 text-uf-red" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-uf-text">{ex.name}</p>
                    <p className="text-xs text-uf-text-muted capitalize">{ex.group} - {ex.equipment}</p>
                  </div>
                </button>
              ))}
              {filtered.length === 0 && (
                <p className="text-center py-8 text-uf-text-muted text-sm">No exercises found</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ============================================
   PLANS TAB
   ============================================ */
const PlansTab = ({ headers }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API}/api/fitness/plans`, { headers });
      if (res.ok) setPlans(await res.json());
    } catch (e) {}
    finally { setLoading(false); }
  };

  const deletePlan = async (planId) => {
    if (!window.confirm('Delete this workout plan?')) return;
    try {
      await fetch(`${API}/api/fitness/plans/${planId}`, { method: 'DELETE', headers });
      fetchPlans();
    } catch (e) {}
  };

  const setActive = async (planId) => {
    try {
      await fetch(`${API}/api/fitness/plans/${planId}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ is_active: true })
      });
      fetchPlans();
    } catch (e) {}
  };

  if (loading) return <div className="text-center py-8 text-uf-text-muted">Loading...</div>;

  if (showCreate || editPlan) {
    return (
      <PlanEditor
        headers={headers}
        plan={editPlan}
        onSave={() => { setShowCreate(false); setEditPlan(null); fetchPlans(); }}
        onCancel={() => { setShowCreate(false); setEditPlan(null); }}
      />
    );
  }

  if (showTemplates) {
    return (
      <TemplateGallery
        headers={headers}
        onBack={() => setShowTemplates(false)}
        onUse={() => { setShowTemplates(false); fetchPlans(); }}
      />
    );
  }

  return (
    <div data-testid="plans-tab">
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setShowCreate(true)}
          className="flex-1 py-3 bg-uf-red text-white font-semibold rounded-xl flex items-center justify-center gap-2 hover:bg-uf-red-hover"
          data-testid="create-plan-btn"
        >
          <Plus className="w-5 h-5" />
          New Plan
        </button>
        <button
          onClick={() => setShowTemplates(true)}
          className="flex-1 py-3 bg-uf-surface border border-uf-border text-uf-text font-semibold rounded-xl flex items-center justify-center gap-2 hover:border-uf-red/50 transition-colors"
          data-testid="browse-templates-btn"
        >
          <Layers className="w-5 h-5 text-uf-red" />
          Templates
        </button>
      </div>

      {plans.length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="w-12 h-12 text-uf-text-muted mx-auto mb-3" />
          <p className="text-uf-text-muted">No plans yet. Create your first workout plan!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {plans.map(plan => (
            <div key={plan.id} className="bg-uf-surface rounded-xl border border-uf-border overflow-hidden" data-testid={`plan-card-${plan.id}`}>
              <button
                onClick={() => setExpandedPlan(expandedPlan === plan.id ? null : plan.id)}
                className="w-full flex items-center justify-between p-4"
              >
                <div className="text-left">
                  <div className="flex items-center gap-2">
                    <p className="text-uf-text font-semibold">{plan.name}</p>
                    {plan.is_active && (
                      <span className="text-[10px] px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">ACTIVE</span>
                    )}
                  </div>
                  <p className="text-xs text-uf-text-muted mt-0.5">
                    {plan.day_count} training days - {plan.rest_days} rest days
                  </p>
                </div>
                {expandedPlan === plan.id ? <ChevronUp className="w-4 h-4 text-uf-text-muted" /> : <ChevronDown className="w-4 h-4 text-uf-text-muted" />}
              </button>

              {expandedPlan === plan.id && (
                <PlanDetailView
                  planId={plan.id}
                  headers={headers}
                  onEdit={() => {
                    fetch(`${API}/api/fitness/plans/${plan.id}`, { headers })
                      .then(r => r.json())
                      .then(d => setEditPlan(d));
                  }}
                  onDelete={() => deletePlan(plan.id)}
                  onSetActive={() => setActive(plan.id)}
                  isActive={plan.is_active}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ============================================
   TEMPLATE GALLERY
   ============================================ */
const LEVEL_COLORS = {
  beginner: 'bg-green-500/20 text-green-400 border-green-500/30',
  intermediate: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  advanced: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const CATEGORY_ICONS = {
  strength: Dumbbell,
  hypertrophy: Zap,
  cardio: Flame,
  general: Target,
};

const TemplateGallery = ({ headers, onBack, onUse }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [using, setUsing] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/fitness/templates`, { headers })
      .then(r => r.json())
      .then(d => setTemplates(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadPreview = async (templateId) => {
    setPreview(templateId);
    try {
      const res = await fetch(`${API}/api/fitness/templates/${templateId}`, { headers });
      if (res.ok) setPreviewData(await res.json());
    } catch (e) {}
  };

  const applyTemplate = async (templateId) => {
    setUsing(true);
    try {
      const res = await fetch(`${API}/api/fitness/plans/from-template/${templateId}`, {
        method: 'POST', headers
      });
      if (res.ok) onUse();
    } catch (e) { console.error(e); }
    finally { setUsing(false); }
  };

  if (loading) return <div className="text-center py-8 text-uf-text-muted">Loading...</div>;

  // Template Preview
  if (preview && previewData) {
    const trainingDays = previewData.days.filter(d => !d.is_rest_day);
    const restDays = previewData.days.filter(d => d.is_rest_day);
    const CatIcon = CATEGORY_ICONS[previewData.category] || Target;

    return (
      <div data-testid="template-preview">
        <button onClick={() => { setPreview(null); setPreviewData(null); }} className="text-uf-text-muted hover:text-uf-text flex items-center gap-1 text-sm mb-4">
          <ChevronLeft className="w-4 h-4" /> Back to Templates
        </button>

        {/* Header Card */}
        <div className="bg-uf-surface rounded-xl border border-uf-border p-5 mb-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-uf-red/10 border border-uf-red/30 flex items-center justify-center shrink-0">
              <CatIcon className="w-6 h-6 text-uf-red" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-uf-text">{previewData.name}</h2>
              <p className="text-sm text-uf-text-muted mt-1">{previewData.description}</p>
              <div className="flex gap-2 mt-3">
                <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${LEVEL_COLORS[previewData.level]}`}>
                  {previewData.level}
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-uf-surface-hi text-uf-text-muted border border-uf-border capitalize">
                  {previewData.category}
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-uf-surface-hi text-uf-text-muted border border-uf-border">
                  {previewData.days_per_week} days/wk
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Weekly Overview */}
        <div className="bg-uf-surface rounded-xl border border-uf-border p-4 mb-4">
          <h3 className="text-sm font-semibold text-uf-text mb-3">Weekly Schedule</h3>
          <div className="space-y-2">
            {previewData.days.map((day, i) => (
              <div key={i} className="flex items-start gap-3 py-2 border-b border-uf-border/30 last:border-0">
                <span className="text-xs text-uf-text-muted w-8 pt-0.5 shrink-0 font-medium">{DAYS[day.day_of_week]}</span>
                {day.is_rest_day ? (
                  <p className="text-sm text-green-400 italic">Rest Day</p>
                ) : (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-uf-red font-semibold">{day.label}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {day.exercises.map((ex, j) => (
                        <span key={j} className="text-[10px] bg-uf-black/80 text-uf-text-muted rounded px-1.5 py-0.5">
                          {ex.exercise_name} {ex.target_sets}x{ex.target_reps}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-uf-surface rounded-lg border border-uf-border p-3 text-center">
            <p className="text-lg font-bold text-uf-text">{trainingDays.length}</p>
            <p className="text-[10px] text-uf-text-muted uppercase">Training</p>
          </div>
          <div className="bg-uf-surface rounded-lg border border-uf-border p-3 text-center">
            <p className="text-lg font-bold text-uf-text">{restDays.length}</p>
            <p className="text-[10px] text-uf-text-muted uppercase">Rest</p>
          </div>
          <div className="bg-uf-surface rounded-lg border border-uf-border p-3 text-center">
            <p className="text-lg font-bold text-uf-text">{trainingDays.reduce((t, d) => t + d.exercises.length, 0)}</p>
            <p className="text-[10px] text-uf-text-muted uppercase">Exercises</p>
          </div>
        </div>

        {/* Use Template Button */}
        <button
          onClick={() => applyTemplate(previewData.id)}
          disabled={using}
          className="w-full py-4 bg-uf-red text-white font-bold rounded-xl text-lg flex items-center justify-center gap-2 hover:bg-uf-red-hover transition-colors disabled:opacity-50"
          data-testid="use-template-btn"
        >
          <Copy className="w-5 h-5" />
          {using ? 'Creating Plan...' : 'Use This Template'}
        </button>
      </div>
    );
  }

  // Template List
  return (
    <div data-testid="template-gallery">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="text-uf-text-muted hover:text-uf-text flex items-center gap-1 text-sm">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <h2 className="text-lg font-bold text-uf-text">Workout Templates</h2>
        <div className="w-12" />
      </div>

      <p className="text-sm text-uf-text-muted mb-4">
        Proven programmes designed by professionals. Pick one and make it yours.
      </p>

      <div className="space-y-3">
        {templates.map(t => {
          const CatIcon = CATEGORY_ICONS[t.category] || Target;
          return (
            <button
              key={t.id}
              onClick={() => loadPreview(t.id)}
              className="w-full bg-uf-surface rounded-xl border border-uf-border p-4 text-left hover:border-uf-red/40 transition-all group"
              data-testid={`template-card-${t.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-uf-red/10 border border-uf-red/20 flex items-center justify-center shrink-0 group-hover:bg-uf-red/20 transition-colors">
                  <CatIcon className="w-5 h-5 text-uf-red" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-uf-text">{t.name}</h3>
                    <ChevronRight className="w-4 h-4 text-uf-text-muted group-hover:text-uf-red transition-colors" />
                  </div>
                  <p className="text-xs text-uf-text-muted mt-1 line-clamp-2">{t.description}</p>
                  <div className="flex gap-2 mt-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${LEVEL_COLORS[t.level]}`}>
                      {t.level}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-uf-surface-hi text-uf-text-muted border border-uf-border">
                      {t.days_per_week} days/wk
                    </span>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};


/* Plan Detail View (inline in PlansTab) */
const PlanDetailView = ({ planId, headers, onEdit, onDelete, onSetActive, isActive }) => {
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/fitness/plans/${planId}`, { headers })
      .then(r => r.json())
      .then(d => setPlan(d))
      .catch(() => {});
  }, [planId]);

  if (!plan) return <div className="p-4 text-sm text-uf-text-muted">Loading...</div>;

  return (
    <div className="px-4 pb-4 border-t border-uf-border/50">
      <div className="mt-3 space-y-2">
        {plan.days?.map((day, i) => (
          <div key={i} className="flex items-start gap-3 py-2">
            <span className="text-xs text-uf-text-muted w-8 pt-0.5">{DAYS[day.day_of_week]}</span>
            <div className="flex-1">
              {day.is_rest_day ? (
                <p className="text-sm text-green-400 italic">Rest Day</p>
              ) : (
                <>
                  <p className="text-sm text-uf-red font-medium">{day.label}</p>
                  {day.exercises?.map((ex, j) => (
                    <p key={j} className="text-xs text-uf-text-muted ml-2">
                      {ex.exercise_name} - {ex.target_sets}x{ex.target_reps}
                    </p>
                  ))}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-4">
        {!isActive && (
          <button onClick={onSetActive} className="flex-1 py-2 bg-green-600 text-white text-sm rounded-lg font-medium" data-testid="set-active-btn">
            Set Active
          </button>
        )}
        <button onClick={onEdit} className="flex-1 py-2 bg-uf-surface-hi text-uf-text text-sm rounded-lg font-medium border border-uf-border" data-testid="edit-plan-btn">
          Edit
        </button>
        <button onClick={onDelete} className="py-2 px-4 text-red-400 text-sm rounded-lg border border-red-500/30" data-testid="delete-plan-btn">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/* ============================================
   PLAN EDITOR - Create/Edit workout plans
   ============================================ */
const PlanEditor = ({ headers, plan, onSave, onCancel }) => {
  const [name, setName] = useState(plan?.name || '');
  const [days, setDays] = useState(() => {
    if (plan?.days) return plan.days;
    return DAYS.map((_, i) => ({
      day_of_week: i, label: '', is_rest_day: false, exercises: []
    }));
  });
  const [activeDay, setActiveDay] = useState(0);
  const [exercises, setExercises] = useState([]);
  const [showExPicker, setShowExPicker] = useState(false);
  const [searchEx, setSearchEx] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/fitness/exercises`, { headers })
      .then(r => r.json())
      .then(d => setExercises(d.exercises || []))
      .catch(() => {});
  }, []);

  const currentDay = days[activeDay];

  const updateDay = (field, value) => {
    setDays(prev => prev.map((d, i) => i === activeDay ? { ...d, [field]: value } : d));
  };

  const addExercise = (ex) => {
    updateDay('exercises', [...(currentDay.exercises || []), {
      exercise_key: ex.key,
      exercise_name: ex.name,
      muscle_group: ex.group,
      target_sets: 3,
      target_reps: '10'
    }]);
    setShowExPicker(false);
  };

  const removeExercise = (idx) => {
    updateDay('exercises', currentDay.exercises.filter((_, i) => i !== idx));
  };

  const updateExercise = (idx, field, value) => {
    const exs = [...currentDay.exercises];
    exs[idx] = { ...exs[idx], [field]: value };
    updateDay('exercises', exs);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const payload = { name, days: days.map(d => ({
        day_of_week: d.day_of_week,
        label: d.label || '',
        is_rest_day: d.is_rest_day,
        sort_order: d.day_of_week,
        exercises: (d.exercises || []).map((e, i) => ({
          exercise_key: e.exercise_key,
          exercise_name: e.exercise_name,
          muscle_group: e.muscle_group,
          target_sets: parseInt(e.target_sets) || 3,
          target_reps: String(e.target_reps || '10'),
          target_weight: e.target_weight ? parseFloat(e.target_weight) : null,
          notes: e.notes || null
        }))
      }))};

      const url = plan?.id ? `${API}/api/fitness/plans/${plan.id}` : `${API}/api/fitness/plans`;
      const method = plan?.id ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      if (res.ok) onSave();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const filteredEx = exercises.filter(e =>
    (groupFilter === 'all' || e.group === groupFilter) &&
    e.name.toLowerCase().includes(searchEx.toLowerCase())
  );

  return (
    <div data-testid="plan-editor">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onCancel} className="text-uf-text-muted hover:text-uf-text flex items-center gap-1 text-sm">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <h2 className="text-lg font-bold text-uf-text">{plan ? 'Edit Plan' : 'New Plan'}</h2>
        <div className="w-12" />
      </div>

      {/* Plan Name */}
      <input
        type="text"
        placeholder="Plan name (e.g. Push Pull Legs)"
        value={name}
        onChange={e => setName(e.target.value)}
        className="w-full bg-uf-surface border border-uf-border rounded-xl px-4 py-3 text-uf-text mb-4 focus:border-uf-red outline-none"
        data-testid="plan-name-input"
      />

      {/* Day Selector */}
      <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1">
        {DAYS.map((d, i) => {
          const day = days[i];
          const hasExercises = day.exercises?.length > 0;
          return (
            <button
              key={d}
              onClick={() => setActiveDay(i)}
              className={`w-11 h-11 rounded-lg text-xs font-medium transition-all shrink-0 relative ${
                activeDay === i
                  ? 'bg-uf-red text-white'
                  : day.is_rest_day
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : hasExercises
                      ? 'bg-uf-surface-hi text-uf-text border border-uf-border'
                      : 'bg-uf-surface text-uf-text-muted border border-uf-border'
              }`}
              data-testid={`editor-day-${d.toLowerCase()}`}
            >
              {d}
              {hasExercises && !day.is_rest_day && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-uf-red text-white text-[9px] rounded-full flex items-center justify-center">
                  {day.exercises.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Day Editor */}
      <div className="bg-uf-surface rounded-xl border border-uf-border p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-uf-text">{FULL_DAYS[activeDay]}</h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-xs text-uf-text-muted">Rest Day</span>
            <div
              onClick={() => updateDay('is_rest_day', !currentDay.is_rest_day)}
              className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${currentDay.is_rest_day ? 'bg-green-500' : 'bg-uf-border'}`}
              data-testid="rest-day-toggle"
            >
              <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${currentDay.is_rest_day ? 'translate-x-5' : ''}`} />
            </div>
          </label>
        </div>

        {!currentDay.is_rest_day && (
          <>
            <input
              type="text"
              placeholder="Day label (e.g. Push Day, Legs)"
              value={currentDay.label || ''}
              onChange={e => updateDay('label', e.target.value)}
              className="w-full bg-uf-black border border-uf-border rounded-lg px-3 py-2 text-sm text-uf-text mb-3 focus:border-uf-red outline-none"
              data-testid="day-label-input"
            />

            {/* Exercise List */}
            <div className="space-y-2 mb-3">
              {(currentDay.exercises || []).map((ex, idx) => (
                <div key={idx} className="bg-uf-black rounded-lg p-3 border border-uf-border/50">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-uf-text font-medium">{ex.exercise_name}</p>
                    <button onClick={() => removeExercise(idx)} className="text-red-400 hover:text-red-300">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="text-[10px] text-uf-text-muted">Sets</label>
                      <input
                        type="number"
                        value={ex.target_sets || ''}
                        onChange={e => updateExercise(idx, 'target_sets', e.target.value)}
                        className="w-full bg-uf-surface border border-uf-border rounded px-2 py-1.5 text-sm text-uf-text text-center"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-[10px] text-uf-text-muted">Reps</label>
                      <input
                        type="text"
                        value={ex.target_reps || ''}
                        onChange={e => updateExercise(idx, 'target_reps', e.target.value)}
                        className="w-full bg-uf-surface border border-uf-border rounded px-2 py-1.5 text-sm text-uf-text text-center"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-[10px] text-uf-text-muted">Weight (kg)</label>
                      <input
                        type="number"
                        value={ex.target_weight || ''}
                        onChange={e => updateExercise(idx, 'target_weight', e.target.value)}
                        placeholder="opt."
                        className="w-full bg-uf-surface border border-uf-border rounded px-2 py-1.5 text-sm text-uf-text text-center"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => setShowExPicker(true)}
              className="w-full py-2 border border-dashed border-uf-border rounded-lg text-uf-text-muted hover:border-uf-red hover:text-uf-red text-sm flex items-center justify-center gap-1 transition-colors"
              data-testid="add-exercise-to-day-btn"
            >
              <Plus className="w-4 h-4" /> Add Exercise
            </button>
          </>
        )}
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={!name.trim() || saving}
        className="w-full py-4 bg-uf-red text-white font-bold rounded-xl text-lg hover:bg-uf-red-hover transition-colors disabled:opacity-50"
        data-testid="save-plan-btn"
      >
        {saving ? 'Saving...' : plan ? 'Update Plan' : 'Create Plan'}
      </button>

      {/* Exercise Picker Modal */}
      {showExPicker && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center">
          <div className="w-full max-w-lg bg-uf-surface border border-uf-border rounded-t-2xl sm:rounded-2xl max-h-[70vh] flex flex-col">
            <div className="p-4 border-b border-uf-border flex items-center justify-between">
              <h3 className="text-lg font-bold text-uf-text">Select Exercise</h3>
              <button onClick={() => setShowExPicker(false)} className="text-uf-text-muted"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-3 border-b border-uf-border">
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-uf-text-muted" />
                <input
                  type="text" placeholder="Search..." value={searchEx}
                  onChange={e => setSearchEx(e.target.value)}
                  className="w-full bg-uf-black border border-uf-border rounded-lg pl-9 pr-3 py-2 text-sm text-uf-text focus:border-uf-red outline-none"
                />
              </div>
              <div className="flex gap-1 overflow-x-auto pb-1">
                {['all', 'chest', 'back', 'shoulders', 'arms', 'legs', 'core', 'cardio'].map(g => (
                  <button key={g} onClick={() => setGroupFilter(g)}
                    className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap ${groupFilter === g ? 'bg-uf-red text-white' : 'bg-uf-black text-uf-text-muted'}`}
                  >{g === 'all' ? 'All' : g.charAt(0).toUpperCase() + g.slice(1)}</button>
                ))}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {filteredEx.map(ex => (
                <button key={ex.key} onClick={() => addExercise(ex)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-uf-surface-hi text-left"
                >
                  <Dumbbell className="w-4 h-4 text-uf-red shrink-0" />
                  <div>
                    <p className="text-sm text-uf-text">{ex.name}</p>
                    <p className="text-xs text-uf-text-muted capitalize">{ex.group}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================================
   HISTORY TAB
   ============================================ */
const HistoryTab = ({ headers }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/fitness/history?limit=30`, { headers })
      .then(r => r.json())
      .then(d => setSessions(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-8 text-uf-text-muted">Loading...</div>;

  if (sessions.length === 0) {
    return (
      <div className="text-center py-12" data-testid="history-empty">
        <Clock className="w-12 h-12 text-uf-text-muted mx-auto mb-3" />
        <p className="text-uf-text-muted">No workout history yet</p>
        <p className="text-sm text-uf-text-muted mt-1">Complete your first workout to see it here</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="history-tab">
      {sessions.map(s => {
        const totalSets = s.exercises?.reduce((t, e) => t + e.sets.length, 0) || 0;
        const duration = s.finished_at
          ? Math.floor((new Date(s.finished_at) - new Date(s.started_at)) / 60000)
          : null;
        const date = new Date(s.started_at);

        return (
          <div key={s.id} className="bg-uf-surface rounded-xl border border-uf-border overflow-hidden" data-testid={`history-${s.id}`}>
            <button
              onClick={() => setExpanded(expanded === s.id ? null : s.id)}
              className="w-full flex items-center justify-between p-4"
            >
              <div className="text-left">
                <p className="text-sm font-semibold text-uf-text">{s.day_label || 'Workout'}</p>
                <p className="text-xs text-uf-text-muted">
                  {date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                  {duration && ` - ${duration} min`}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm font-bold text-uf-text">{totalSets} sets</p>
                  <p className="text-xs text-uf-text-muted">{s.exercises?.length || 0} exercises</p>
                </div>
                {expanded === s.id ? <ChevronUp className="w-4 h-4 text-uf-text-muted" /> : <ChevronDown className="w-4 h-4 text-uf-text-muted" />}
              </div>
            </button>

            {expanded === s.id && (
              <div className="px-4 pb-4 border-t border-uf-border/50 space-y-3 mt-2">
                {s.exercises?.map((ex, i) => (
                  <div key={i}>
                    <p className="text-sm text-uf-red font-medium mb-1">{ex.exercise_name}</p>
                    {ex.sets.map((set, j) => (
                      <div key={j} className={`flex gap-4 text-xs py-1 ${set.is_pr ? 'text-yellow-400' : 'text-uf-text-muted'}`}>
                        <span className="w-12">Set {set.set_number}</span>
                        <span className="w-16">{set.weight ? `${set.weight}kg` : '-'}</span>
                        <span className="w-12">{set.reps ? `${set.reps} reps` : '-'}</span>
                        {set.is_pr && <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ============================================
   PERSONAL BESTS TAB
   ============================================ */
const PersonalBestsTab = ({ headers }) => {
  const [pbs, setPbs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/fitness/personal-bests`, { headers })
      .then(r => r.json())
      .then(d => setPbs(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-8 text-uf-text-muted">Loading...</div>;

  if (pbs.length === 0) {
    return (
      <div className="text-center py-12" data-testid="pbs-empty">
        <Trophy className="w-12 h-12 text-uf-text-muted mx-auto mb-3" />
        <p className="text-uf-text-muted">No personal bests yet</p>
        <p className="text-sm text-uf-text-muted mt-1">Start logging sets to track your PRs</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="pbs-tab">
      {pbs.map((pb, i) => (
        <div key={i} className="bg-uf-surface rounded-xl border border-uf-border p-4 flex items-center gap-4" data-testid={`pb-${pb.exercise_key}`}>
          <div className="w-10 h-10 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-yellow-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-uf-text truncate">{pb.exercise_name}</p>
            <p className="text-xs text-uf-text-muted">
              {pb.achieved_at ? new Date(pb.achieved_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
            </p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-yellow-500">{pb.best_weight}kg</p>
            <p className="text-xs text-uf-text-muted">{pb.best_reps} reps</p>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ============================================
   BODY TAB - Measurements & Weight Tracking
   ============================================ */
const BodyTab = ({ headers, onStatsChange }) => {
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    weight_kg: '', body_fat_pct: '', chest_cm: '', waist_cm: '',
    hips_cm: '', bicep_left_cm: '', bicep_right_cm: '',
    thigh_left_cm: '', thigh_right_cm: '', notes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchMeasurements(); }, []);

  const fetchMeasurements = async () => {
    try {
      const res = await fetch(`${API}/api/fitness/measurements`, { headers });
      if (res.ok) setMeasurements(await res.json());
    } catch (e) {}
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== '' && v !== null) {
        payload[k] = k === 'notes' ? v : parseFloat(v);
      }
    });
    if (Object.keys(payload).length === 0) { setSaving(false); return; }

    try {
      const res = await fetch(`${API}/api/fitness/measurements`, {
        method: 'POST', headers, body: JSON.stringify(payload)
      });
      if (res.ok) {
        setShowForm(false);
        setForm({ weight_kg: '', body_fat_pct: '', chest_cm: '', waist_cm: '', hips_cm: '', bicep_left_cm: '', bicep_right_cm: '', thigh_left_cm: '', thigh_right_cm: '', notes: '' });
        fetchMeasurements();
        onStatsChange();
      }
    } catch (e) {}
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-center py-8 text-uf-text-muted">Loading...</div>;

  // Weight chart (simple SVG)
  const weightData = measurements.filter(m => m.weight_kg).slice(0, 20).reverse();

  return (
    <div data-testid="body-tab">
      <button
        onClick={() => setShowForm(!showForm)}
        className="w-full py-3 bg-uf-red text-white font-semibold rounded-xl flex items-center justify-center gap-2 mb-4 hover:bg-uf-red-hover"
        data-testid="log-measurement-btn"
      >
        <Plus className="w-5 h-5" />
        Log Measurement
      </button>

      {showForm && (
        <div className="bg-uf-surface rounded-xl border border-uf-border p-4 mb-4" data-testid="measurement-form">
          <h3 className="text-sm font-semibold text-uf-text mb-3">New Measurement</h3>
          <div className="grid grid-cols-2 gap-2">
            {[
              { key: 'weight_kg', label: 'Weight (kg)' },
              { key: 'body_fat_pct', label: 'Body Fat (%)' },
              { key: 'chest_cm', label: 'Chest (cm)' },
              { key: 'waist_cm', label: 'Waist (cm)' },
              { key: 'hips_cm', label: 'Hips (cm)' },
              { key: 'bicep_left_cm', label: 'L Bicep (cm)' },
              { key: 'bicep_right_cm', label: 'R Bicep (cm)' },
              { key: 'thigh_left_cm', label: 'L Thigh (cm)' },
              { key: 'thigh_right_cm', label: 'R Thigh (cm)' },
            ].map(f => (
              <div key={f.key}>
                <label className="text-[10px] text-uf-text-muted uppercase">{f.label}</label>
                <input
                  type="number" step="0.1"
                  value={form[f.key]}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                  className="w-full bg-uf-black border border-uf-border rounded-lg px-3 py-2 text-sm text-uf-text focus:border-uf-red outline-none"
                  data-testid={`input-${f.key}`}
                />
              </div>
            ))}
          </div>
          <div className="mt-2">
            <label className="text-[10px] text-uf-text-muted uppercase">Notes</label>
            <input
              type="text"
              value={form.notes}
              onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full bg-uf-black border border-uf-border rounded-lg px-3 py-2 text-sm text-uf-text focus:border-uf-red outline-none"
              placeholder="Optional notes"
              data-testid="input-notes"
            />
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={handleSave} disabled={saving}
              className="flex-1 py-2.5 bg-uf-red text-white rounded-lg font-medium text-sm disabled:opacity-50"
              data-testid="save-measurement-btn"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => setShowForm(false)}
              className="py-2.5 px-4 text-uf-text-muted border border-uf-border rounded-lg text-sm"
            >Cancel</button>
          </div>
        </div>
      )}

      {/* Weight Chart */}
      {weightData.length >= 2 && (
        <div className="bg-uf-surface rounded-xl border border-uf-border p-4 mb-4" data-testid="weight-chart">
          <h3 className="text-sm font-semibold text-uf-text mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-uf-red" />
            Weight Progress
          </h3>
          <WeightChart data={weightData} />
        </div>
      )}

      {/* Measurement History */}
      {measurements.length === 0 ? (
        <div className="text-center py-12">
          <Ruler className="w-12 h-12 text-uf-text-muted mx-auto mb-3" />
          <p className="text-uf-text-muted">No measurements logged yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {measurements.map((m, i) => {
            const prev = measurements[i + 1];
            const weightDiff = m.weight_kg && prev?.weight_kg ? (m.weight_kg - prev.weight_kg).toFixed(1) : null;
            return (
              <div key={m.id} className="bg-uf-surface rounded-xl border border-uf-border p-4" data-testid={`measurement-${m.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-uf-text-muted">
                    {new Date(m.recorded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                  {weightDiff && (
                    <span className={`text-xs font-medium ${parseFloat(weightDiff) > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {parseFloat(weightDiff) > 0 ? '+' : ''}{weightDiff}kg
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {m.weight_kg && <Stat label="Weight" value={`${m.weight_kg}kg`} />}
                  {m.body_fat_pct && <Stat label="Body Fat" value={`${m.body_fat_pct}%`} />}
                  {m.chest_cm && <Stat label="Chest" value={`${m.chest_cm}cm`} />}
                  {m.waist_cm && <Stat label="Waist" value={`${m.waist_cm}cm`} />}
                  {m.hips_cm && <Stat label="Hips" value={`${m.hips_cm}cm`} />}
                  {(m.bicep_left_cm || m.bicep_right_cm) && (
                    <Stat label="Biceps" value={`${m.bicep_left_cm || '-'}/${m.bicep_right_cm || '-'}cm`} />
                  )}
                </div>
                {m.notes && <p className="text-xs text-uf-text-muted mt-2 italic">{m.notes}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <p className="text-xs text-uf-text-muted">{label}</p>
    <p className="text-sm font-semibold text-uf-text">{value}</p>
  </div>
);

/* Simple SVG Weight Chart */
const WeightChart = ({ data }) => {
  if (data.length < 2) return null;
  const weights = data.map(d => d.weight_kg);
  const min = Math.min(...weights) - 1;
  const max = Math.max(...weights) + 1;
  const range = max - min || 1;
  const w = 320;
  const h = 120;
  const padding = 20;
  const chartW = w - padding * 2;
  const chartH = h - padding * 2;

  const points = data.map((d, i) => ({
    x: padding + (i / (data.length - 1)) * chartW,
    y: padding + chartH - ((d.weight_kg - min) / range) * chartH,
  }));

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ maxHeight: 140 }}>
      {/* Grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map((f, i) => (
        <line key={i} x1={padding} y1={padding + chartH * (1 - f)} x2={w - padding} y2={padding + chartH * (1 - f)}
          stroke="#262626" strokeWidth="1" />
      ))}
      {/* Y labels */}
      <text x={padding - 5} y={padding + 4} fill="#A1A1A1" fontSize="8" textAnchor="end">{max.toFixed(0)}</text>
      <text x={padding - 5} y={h - padding + 4} fill="#A1A1A1" fontSize="8" textAnchor="end">{min.toFixed(0)}</text>
      {/* Line */}
      <path d={pathD} fill="none" stroke="#E31837" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Dots */}
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="3" fill="#E31837" />
      ))}
      {/* X labels (first and last) */}
      <text x={points[0].x} y={h - 4} fill="#A1A1A1" fontSize="7" textAnchor="middle">
        {new Date(data[0].recorded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
      </text>
      <text x={points[points.length - 1].x} y={h - 4} fill="#A1A1A1" fontSize="7" textAnchor="middle">
        {new Date(data[data.length - 1].recorded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
      </text>
    </svg>
  );
};

export default FitnessTracker;
